import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectActiveListId,
  selectCurrentTrip,
  selectListOrder,
  selectOrderedTopLevelItems,
} from '../../../redux/trip/trip.selectors';
import { updateActiveListId } from '../../../redux/trip/trip.slice';
import { canEditTrip } from '../../../redux/trip/trip.utils';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import AddListInput from './components/AddListInput/AddListInput';
import DividerItem from './components/DividerItem/DividerItem';
import ListItem from './components/ListItem/ListItem';
import TopLevelDropGap from './components/TopLevelDropGap/TopLevelDropGap';
import { SidebarUIContext } from './SidebarUIContext';
import './TripSidebar.scss';
import { useToggleSet } from '../../../hooks/useToggleSet';
import { store } from '../../../redux/store';

function TripSidebar() {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const trip = useAppSelector(selectCurrentTrip);
  const canEdit = Boolean(canEditTrip(trip, currentUser));
  const activeListId = useAppSelector(selectActiveListId);
  const listOrder = useAppSelector(selectListOrder);
  const orderedTopLevelItems = useAppSelector(selectOrderedTopLevelItems);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  // Initialise synchronously from the store so dividers are already collapsed on
  // the first render — avoids the post-paint flash caused by a useEffect approach.
  const [collapsedDividerIds, toggleDivider] = useToggleSet(() => {
    const items = selectOrderedTopLevelItems(store.getState());
    return items
      .filter((item) => item.itemType === 'divider')
      .map((item) => item.data.id)
      .slice(1); // keep the first divider expanded
  });
  const [openEmojiId, setOpenEmojiId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Auto-select the first list when no active list is set (e.g. first load via a divider)
  useEffect(() => {
    if (activeListId || !orderedTopLevelItems.length) {
      return;
    }
    const firstItem = orderedTopLevelItems[0];
    if (firstItem.itemType === 'divider' && firstItem.data.listOrder.length > 0) {
      dispatch(updateActiveListId(firstItem.data.listOrder[0]));
    }
  }, [orderedTopLevelItems, activeListId, dispatch]);

  useEffect(() => {
    if (searchParams.has('list')) {
      dispatch(updateActiveListId(searchParams.get('list') || ''));
    }
  }, [searchParams, dispatch]);

  return (
    <SidebarUIContext.Provider value={{ openEmojiId, setOpenEmojiId, openMenuId, setOpenMenuId }}>
      <div className='list-container-div'>
        <AddListInput
          tripId={trip?.id}
          canEdit={canEdit}
          listOrder={listOrder}
        />
        {orderedTopLevelItems.length === 0 ? (
          <div className='no-list-message'>
            {' '}
            Create a list by clicking the &apos;+&apos; button above. Once created, a search bar
            will appear above the map for you to add places to your list!
          </div>
        ) : (
          <div className='list-scrollable-div'>
            {trip && (
              <SortableContext
                items={listOrder || []}
                strategy={verticalListSortingStrategy}
              >
                <TopLevelDropGap insertIndex={0} />
                {orderedTopLevelItems.map((item, i) => {
                  const rendered =
                    item.itemType === 'list' ? (
                      <ListItem
                        key={item.data.id}
                        tripId={trip.id}
                        list={item.data}
                        listOrder={listOrder || []}
                        isActive={item.data.id === activeListId}
                        canEdit={canEdit}
                        isEditingName={item.data.id === editingListId}
                        onStartEditing={() => setEditingListId(item.data.id)}
                        onFinishEditing={() => setEditingListId(null)}
                        cardCount={item.data.cardOrder?.length || 0}
                      />
                    ) : (
                      <DividerItem
                        key={item.data.id}
                        tripId={trip.id}
                        divider={item.data}
                        listOrder={listOrder || []}
                        trip={trip}
                        activeListId={activeListId}
                        canEdit={canEdit}
                        isExpanded={!collapsedDividerIds.has(item.data.id)}
                        onToggle={() => toggleDivider(item.data.id)}
                        editingListId={editingListId}
                        onStartEditingList={setEditingListId}
                        onFinishEditingList={() => setEditingListId(null)}
                      />
                    );
                  return (
                    <React.Fragment key={item.data.id}>
                      {rendered}
                      <TopLevelDropGap insertIndex={i + 1} />
                    </React.Fragment>
                  );
                })}
              </SortableContext>
            )}
          </div>
        )}
      </div>
    </SidebarUIContext.Provider>
  );
}

TripSidebar.displayName = 'TripSidebar';
export default TripSidebar;
